import React from "react";
import { Modal, ModalBody, ModalHeader, ModalFooter } from "reactstrap";
import { snapshot, useSnapshot } from "valtio";
import { useTranslation } from "react-i18next";
import { CheckboxIcon, CrossIcon, StyledButton } from "@aureskonnect/react-ui";
import { toast } from "react-toastify";
import { mutate } from "swr";
import { diff } from "deep-diff";
import classnames from "classnames";
import moment from "moment";

import {
    computedStore,
    generalConfigStore,
    setChosenTemplate,
    setInitItemsFromGeneralSetting,
    setIsInformationModesEmpty,
    setIsModalOpened,
    setIsProjectModesModalOpened,
    setProject,
} from "@store";
import { store as useStore } from "@components/VerticalLayout/store";
import {
    setIsLoading,
    setIsModalConfirmationConfigurationKiosk,
    setKioskProject,
    store,
} from "../../../store/project";
import {
    setKey,
    store as localStore,
    setDifferenceBetweenTwoObjects,
} from "@pages/Project/store";

import { SwiperWrapper } from "../../FullscreenPreview/SwiperWrapper";
import { Modal as Modala } from "./Modal";
import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { moveArrayElementToTheBegin } from "../../../helpers/general";

import "./index.css";
import { useSelector } from "react-redux";
import { useFetchGlobal } from "../../../api/hooks";

const API_BASE = process.env.REACT_APP_API_V2_URL;
const EXCLUDED_SALE_METHOD_ID = "f25e7c96-b5d3-4f2e-acd0-1500258283c2";
const TOAST_CONFIG = {
    position: toast.POSITION.TOP_CENTER,
    autoClose: 2000,
    theme: "colored" as const,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
};

type PropsType = {
    children: React.ReactNode;
    title: string;
    rowData?: any;
    setIsDataUpdated: Function;
    isDataUpdated: boolean | number;
    setIsCreatedProject: Function;
    setSelectedRows: Function;
};

interface DiffEntry {
    path?: (string | number)[];
    kind?: string;
    lhs?: any;
    rhs?: any;
    [key: string]: any;
}

interface ContentItem {
    id: string;
    [key: string]: any;
}

interface TemplateStructure {
    template?: {
        content?: {
            [key: string]: {
                items?: ContentItem[];
            };
        };
    };
}

const showSuccessToast = (t: Function, message: string) => {
    toast.success(t(message), {
        ...TOAST_CONFIG,
        icon: <CheckboxIcon height={25} width={25} fill="white" />,
    });
};

const showErrorToast = (t: Function) => {
    toast.error(t("There's an error"), TOAST_CONFIG);
};

const showWarningToast = (t: Function) => {
    toast.warning(
        t(
            "Please note that the assignment could not be made to one or more stores. Please check the store(s) data settings."
        ),
        {
            ...TOAST_CONFIG,
            icon: <CheckboxIcon height={25} width={25} fill="white" />,
        }
    );
};

const hasValidInformationModes = (
    informationModes: ProjectMainContentItemType[],
    saleMethods: any[]
): boolean =>
    informationModes.length > 0 &&
    informationModes.some((item) =>
        saleMethods.some(
            (saleMethod) =>
                saleMethod.active &&
                Object.keys(item.active).includes(saleMethod.name)
        )
    );

const orderLanguages = (
    languages: LanguagesItemType[]
): LanguagesItemType[] => {
    const activeNonDefault = languages.filter((l) => l.active && !l.isDefault);
    if (activeNonDefault.length <= 1) return languages;

    return moveArrayElementToTheBegin(
        languages.find((l) => l.isDefault),
        moveArrayElementToTheBegin(
            languages.find((l) => l.active && !l.isDefault),
            languages
        )
    );
};

const buildFileFromItem = (item: ProjectMainContentItemType) => ({
    id: item.id,
    name: item.name,
    content: Object.fromEntries(
        Object.entries(item.languages).map(([key, value]) => [
            key,
            { path: value.content as string, defaultImage: value.content },
        ])
    ),
});

const buildLanguageFile = (item: LanguagesItemType) => ({
    id: item.id,
    name: item.name,
    content: item.flag,
    defaultImage: item.flag,
});

const updateFilesWithMissingLanguages = (
    files: any[],
    languages: LanguagesItemType[]
): any[] =>
    files.map((file) => {
        if (file.content && typeof file.content === "object") {
            const missingLanguages = languages.filter(
                (lang) =>
                    !Object.prototype.hasOwnProperty.call(
                        file.content,
                        lang.name
                    )
            );

            if (missingLanguages.length > 0) {
                const additionalContent = Object.fromEntries(
                    missingLanguages.map((lang) => [lang.name, file.content.fr])
                );
                return {
                    ...file,
                    content: { ...file.content, ...additionalContent },
                };
            }
        }
        return file;
    });

const buildFilesTemplate = (
    baseFiles: any[],
    saleMethods: ProjectMainContentItemType[],
    informationModes: ProjectMainContentItemType[],
    reglements: any[],
    languages: LanguagesItemType[],
    logo: any
): any[] => {
    const files = [
        ...baseFiles,
        ...saleMethods.map(buildFileFromItem),
        ...informationModes.map(buildFileFromItem),
        ...reglements
            .filter(
                (item) => item.name !== "At the counter" && item.name !== "BDP"
            )
            .map(buildFileFromItem),
        ...languages.map(buildLanguageFile),
        {
            id: logo.id,
            key: "Logo",
            name: logo.name,
            content: logo.content,
            defaultImage: logo.content,
        },
    ];

    return updateFilesWithMissingLanguages(files, languages);
};

const processDifferenceFlags = (differences: any[]) =>
    differences.reduce(
        (flags, item: any) => {
            const path = item.path ?? [];
            if (path.includes("content") && path.includes("languages")) {
                flags.franchiseFavorite = true;
            }
            if (path.includes("standbyScreen")) {
                flags.standbyScreen = true;
            }
            if (path.includes("standbyStateScreen")) {
                flags.standbyStateScreen = true;
            }
            return flags;
        },
        {
            franchiseFavorite: false,
            standbyScreen: false,
            standbyStateScreen: false,
        }
    );

const buildUpdatedDifferences = (
    differences: any[],
    kioskProject: any,
    flags: {
        franchiseFavorite: boolean;
        standbyScreen: boolean;
        standbyStateScreen: boolean;
    }
) => {
    let updatedDifferences = differences.filter(
        (item: any) =>
            !item.path?.includes("standbyScreen") &&
            !(
                flags.standbyStateScreen &&
                item.path?.includes("standbyStateScreen")
            )
    );

    if (flags.franchiseFavorite) {
        const favoredFranchise = (kioskProject.template.content
            .languages as LanguagesType)?.items;
        if (favoredFranchise) updatedDifferences.push({ favoredFranchise });
    }

    if (flags.standbyScreen) {
        const standbyScreen = (kioskProject.template.pages.ways
            .standbyScreen as StandbyScreenPageType)?.items;
        if (standbyScreen) updatedDifferences.push({ standbyScreen });
    }

    if (flags.standbyStateScreen) {
        const standbyStateScreen = (kioskProject.template.content
            .standbyStateScreen as StandbyStateScreenItemsType)?.items;
        if (standbyStateScreen) updatedDifferences.push({ standbyStateScreen });
    }

    return updatedDifferences;
};

export function ModalWrapper({
    children,
    title = "",
    rowData,
    setIsDataUpdated,
    isDataUpdated,
    setIsCreatedProject,
    setSelectedRows,
}: PropsType): JSX.Element {
    const { fetchGlobal } = useFetchGlobal();
    const { t } = useTranslation();
    const { initialProject } = useSnapshot(localStore);
    const {
        userID,
        franchiseID,
        projectId,
        shopID,
        oneShop,
        operatorID,
    } = useSnapshot(useStore);
    const uuidUser = localStorage.getItem("uuidUser");
    const {
        isModalOpened,
        isWaysTabActive,
        itemsDisable,
        isFieldRequiredEmpty,
        project,
    } = useSnapshot(generalConfigStore);
    const { isConsult } = useSnapshot(store);
    const { items } = useSnapshot(computedStore);
    const [isModalShown, setModalVisibility] = React.useState(false);
    const [isDisabled, setIsDisabled] = React.useState(false);
    const mainColor = useSelector(
        (state: any) => state.global.infoUser.maincolor
    );

    const fetchShopData = async (endpoint: string, shopId: string) =>
        fetchGlobal(
            `${API_BASE}/settings/consomation/${endpoint}?franchiseId=${franchiseID}&shopId=${shopId}`
        );

    const needsConfirmationModal = (kioskProject: any) =>
        rowData !== undefined &&
        Object.keys(rowData).length !== 0 &&
        rowData.template?.id !== "" &&
        rowData?.subRows?.length > 0 &&
        (diff(rowData.template, kioskProject.template) !== undefined ||
            diff(rowData.files, kioskProject.files) !== undefined);

    const toggle = () => {
        if (project.template.id === "") {
            setKey(Math.floor(Math.random() * 10000));
        }
        setIsModalOpened(!isModalOpened);
        setIsCreatedProject(false);
        setIsProjectModesModalOpened(false);
        setSelectedRows([]);
    };

    const closeModal = () => setModalVisibility(false);

    const handleUpdateProjectButtonOnClickEvent = async () => {
        const apiUrl = `${API_BASE}/settings/project/template`;
        const kioskProject = generalConfigStore.project;
        setKioskProject(kioskProject);
        setIsLoading(true);

        try {
            if (Object.keys(rowData).length > 0 && rowData.template.id !== "") {
                let differences = removeMirroredDiffs(
                    processDifferences(
                        JSON.parse(JSON.stringify(initialProject)),
                        JSON.parse(JSON.stringify(kioskProject))
                    )
                );
                const flags = processDifferenceFlags(differences);
                setDifferenceBetweenTwoObjects(
                    buildUpdatedDifferences(differences, kioskProject, flags)
                );
            }

            if (!oneShop) {
                const hasChanges =
                    rowData !== undefined &&
                    Object.keys(rowData).length !== 0 &&
                    rowData.template.id !== "" &&
                    rowData?.subRows?.length > 0 &&
                    (diff(
                        JSON.parse(JSON.stringify(initialProject)).template,
                        kioskProject.template
                    ) !== undefined ||
                        diff(
                            JSON.parse(JSON.stringify(initialProject)).files,
                            kioskProject.files
                        ) !== undefined);

                if (hasChanges) {
                    setIsModalConfirmationConfigurationKiosk(true);
                    setIsLoading(false);
                    return;
                }

                const result: any = await fetchGlobal(apiUrl, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        userId: userID,
                        franchiseId: franchiseID,
                        projectId:
                            Object.keys(rowData).length === 0
                                ? projectId
                                : rowData.projectId ?? rowData.id_project,
                        project_id: "PROJECT",
                        shopId:
                            Object.keys(rowData).length === 0
                                ? oneShop
                                    ? shopID
                                    : "0"
                                : rowData.shopId.toString(),
                        operatorId: operatorID,
                        uuidUser,
                        modified_at: moment().format("DD/MM/YYYY [at] h:mm a"),
                        data: {
                            template: kioskProject.template,
                            files: kioskProject.files,
                        },
                    }),
                });

                mutate(apiUrl, result);
                if (result?.error) throw new Error(result.message);

                setIsModalOpened(!isModalOpened);

                if (!oneShop) {
                    showSuccessToast(t, "Template saved successfully");
                } else if (
                    kioskProject.associates &&
                    Object.keys(kioskProject.associates).length > 0
                ) {
                    await handleSaveTemplateOnClickEvent();
                }

                setKey(Math.floor(Math.random() * 10000));
            } else if (
                kioskProject.associates &&
                Object.keys(kioskProject.associates).length > 0
            ) {
                await handleSaveTemplateOnClickEvent();
            }
        } catch (error) {
            console.error(error);
            showErrorToast(t);
        } finally {
            setIsLoading(false);
        }
    };

    const firstConfiguration = async () => {
        const apiUrl = `${API_BASE}/settings/project/template/first`;
        const kioskProject = generalConfigStore.project;
        setKioskProject(kioskProject);
        setIsLoading(true);

        try {
            const shopIds = rowData.subRows?.reduce(
                (acc: any, el: any) => ({
                    ...acc,
                    [el.shopId]: el[t("Designation")],
                }),
                { "0": "franchise" }
            ) ?? { "0": "franchise" };

            const result: any = await fetchGlobal(apiUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    userId: userID,
                    franchiseId: franchiseID,
                    projectId:
                        Object.keys(rowData).length === 0
                            ? projectId
                            : rowData.projectId ?? rowData.id_project,
                    project_id: "PROJECT",
                    shopId: shopIds,
                    operatorId: operatorID,
                    uuidUser,
                    modified_at: moment().format("DD/MM/YYYY [at] h:mm a"),
                    designation: project.name,
                    data: {
                        template: kioskProject.template,
                        files: kioskProject.files,
                    },
                }),
            });

            mutate(apiUrl, result);
            if (result?.error) throw new Error(result.message);

            setIsModalOpened(!isModalOpened);

            if (!oneShop) {
                showSuccessToast(t, "Template saved successfully");
            } else if (
                kioskProject.associates &&
                Object.keys(kioskProject.associates).length > 0
            ) {
                await handleSaveTemplateOnClickEvent();
            }

            setKey(Math.floor(Math.random() * 10000));
        } catch (error) {
            console.error(error);
            showErrorToast(t);
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpdateProjectShopsButtonOnClickEvent = async () => {
        setIsLoading(true);
        const apiUrl = `${API_BASE}/settings/project/template`;
        const kioskProject = generalConfigStore.project;
        setKioskProject(kioskProject);

        try {
            if (
                Object.keys(kioskProject).length > 0 &&
                kioskProject.template.id !== ""
            ) {
                let differences = removeMirroredDiffs(
                    processDifferences(
                        JSON.parse(JSON.stringify(initialProject)),
                        kioskProject
                    )
                );
                const flags = processDifferenceFlags(differences);
                setDifferenceBetweenTwoObjects(
                    buildUpdatedDifferences(differences, kioskProject, flags)
                );
            }

            if (needsConfirmationModal(kioskProject)) {
                setIsModalConfirmationConfigurationKiosk(true);
                setIsLoading(false);
                return;
            }

            const result: any = await fetchGlobal(apiUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    userId: userID,
                    franchiseId: franchiseID,
                    project_id: "PROJECT",
                    projectId: kioskProject.projectId,
                    shopId: shopID,
                    operatorId: operatorID,
                    uuidUser,
                    modified_at: moment().format("DD/MM/YYYY [at] h:mm a"),
                    data: {
                        template: kioskProject.template,
                        files: kioskProject.files,
                    },
                }),
            });

            mutate(apiUrl, result);
            if (result?.error) throw new Error(result.message);

            showSuccessToast(t, "Template saved successfully");
            setKey(Math.floor(Math.random() * 10000));
        } catch (error) {
            console.error(error);
            showErrorToast(t);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveTemplateOnClickEvent = async () => {
        try {
            const kioskProject = generalConfigStore.project;

            for (const shopId of Object.keys(kioskProject.associates)) {
                const [
                    languages,
                    saleMethods,
                    reglements,
                    informationModes,
                ] = (await Promise.all([
                    fetchShopData("langue", shopId),
                    fetchShopData("sale", shopId),
                    fetchShopData("reglement", shopId),
                    fetchShopData("information", shopId),
                ])) as [
                    LanguagesItemType[],
                    any[],
                    any[],
                    ProjectMainContentItemType[]
                ];

                const hasActiveSaleMethod = saleMethods.some(
                    (item) => item.active && item.id !== EXCLUDED_SALE_METHOD_ID
                );
                const hasActiveLanguage = languages.some((item) => item.active);

                if (!hasActiveSaleMethod || !hasActiveLanguage) {
                    showWarningToast(t);
                    return;
                }

                setIsInformationModesEmpty(
                    !hasValidInformationModes(informationModes, saleMethods)
                );

                const template = {
                    ...kioskProject.template,
                    content: {
                        ...kioskProject.template.content,
                        languages: { items: orderLanguages(languages) },
                        salesMethods: { items: saleMethods },
                        meansOfPayment: { items: reglements },
                        informationModes: { items: informationModes },
                    },
                };

                const updatedProject = { ...kioskProject, template };

                setProject(updatedProject);
                setInitItemsFromGeneralSetting({
                    informationModes,
                    saleMethods,
                    reglements,
                    languages,
                });
                setChosenTemplate(template, kioskProject.files);

                const apiUrl = `${API_BASE}/settings/project/template`;
                const result: any = await fetchGlobal(apiUrl, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        userId: userID,
                        franchiseId: franchiseID,
                        projectId: updatedProject.projectId,
                        project_id: "PROJECT",
                        shopId,
                        data: updatedProject,
                        operatorId: operatorID,
                        uuidUser,
                    }),
                });

                mutate(apiUrl, result);
                if (result?.error) throw new Error(result.message);
            }

            setIsModalOpened(false);
        } catch (error) {
            console.error(error);
            showErrorToast(t);
        }
    };

    const handleSaveTemplateFromShopsToFranchiseOnClickEvent = async (
        shops: any[]
    ) => {
        let isEmpty = false;
        let isSuccess = false;
        setIsLoading(true);

        try {
            const kioskProject = generalConfigStore.project;

            for (const shopId of shops) {
                let project1: any = null;

                if (shopId === "0") {
                    const [
                        languages,
                        saleMethods,
                        reglements,
                        informationModes,
                        logo,
                    ] = (await Promise.all([
                        fetchShopData("langue", shopId),
                        fetchShopData("sale", shopId),
                        fetchShopData("reglement", shopId),
                        fetchShopData("information", shopId),
                        fetchShopData("logo", shopId),
                    ])) as [
                        LanguagesItemType[],
                        any[],
                        any[],
                        ProjectMainContentItemType[],
                        any
                    ];

                    const hasActiveSaleMethod = saleMethods.some(
                        (item) =>
                            item.active && item.id !== EXCLUDED_SALE_METHOD_ID
                    );
                    const hasActiveLanguage = languages.some(
                        (item) => item.active
                    );

                    if (!hasActiveSaleMethod || !hasActiveLanguage) {
                        isEmpty = true;
                        continue;
                    }

                    setIsInformationModesEmpty(
                        !hasValidInformationModes(informationModes, saleMethods)
                    );

                    const template = {
                        ...kioskProject.template,
                        content: {
                            ...kioskProject.template.content,
                            languages: {
                                ...kioskProject.template.content.languages,
                                items: orderLanguages(languages),
                            },
                            salesMethods: { items: saleMethods },
                            meansOfPayment: { items: reglements },
                            informationModes: { items: informationModes },
                            generalDesign: {
                                ...kioskProject.template.content.generalDesign,
                                logo,
                            },
                        },
                    };

                    const filesTemplate = buildFilesTemplate(
                        kioskProject.files,
                        saleMethods,
                        informationModes,
                        reglements,
                        languages,
                        logo
                    );

                    setProject({
                        ...kioskProject,
                        template,
                        files: filesTemplate,
                    });
                    setInitItemsFromGeneralSetting({
                        informationModes,
                        saleMethods,
                        reglements,
                        languages,
                        logo,
                    });
                    setChosenTemplate(template, filesTemplate);

                    project1 = {
                        ...kioskProject,
                        template,
                        files: filesTemplate,
                    };
                }

                if (needsConfirmationModal(kioskProject)) {
                    setIsModalConfirmationConfigurationKiosk(true);
                    setIsLoading(false);
                    return;
                }

                const apiUrl = `${API_BASE}/settings/project/template`;
                const isRowDataEmpty = Object.keys(rowData).length === 0;

                const result: any = await fetchGlobal(apiUrl, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        userId: userID,
                        franchiseId: franchiseID,
                        projectId: isRowDataEmpty
                            ? projectId
                            : shopId === "0"
                            ? project1?.projectId
                            : kioskProject.projectId,
                        project_id: "PROJECT",
                        shopId,
                        operatorId: operatorID,
                        uuidUser,
                        modified_at: moment().format("DD/MM/YYYY [at] h:mm a"),
                        data: {
                            template:
                                shopId === "0"
                                    ? project1?.template
                                    : kioskProject.template,
                            files:
                                shopId === "0"
                                    ? project1?.files
                                    : kioskProject.files,
                        },
                    }),
                });

                if (result?.error) throw new Error(result.message);

                mutate(apiUrl, result);
                isSuccess = true;
            }
        } catch (error) {
            console.error("Error saving template:", error);
            showErrorToast(t);
        } finally {
            setIsLoading(false);
            if (isEmpty) showWarningToast(t);
            if (isSuccess) showSuccessToast(t, "Template saved successfully");
        }
    };

    const getDataFromGeneralSetting = async (
        shopId: string,
        templateProject: any
    ): Promise<any | undefined> => {
        try {
            const [
                languages,
                saleMethods,
                reglements,
                informationModes,
            ] = (await Promise.all([
                fetchShopData("langue", shopId),
                fetchShopData("sale", shopId),
                fetchShopData("reglement", shopId),
                fetchShopData("information", shopId),
            ])) as [
                LanguagesItemType[],
                any[],
                any[],
                ProjectMainContentItemType[]
            ];

            const hasActiveSaleMethod = saleMethods.some(
                (item) => item.active && item.id !== EXCLUDED_SALE_METHOD_ID
            );
            const hasActiveLanguage = languages.some((item) => item.active);

            if (!hasActiveSaleMethod || !hasActiveLanguage) return undefined;

            const templateSalesMethods =
                templateProject.content.salesMethods.items;
            const templateReglements =
                templateProject.content.meansOfPayment.items;
            const templateLanguages = templateProject.content.languages.items;
            const templateInfoModes =
                templateProject.content.informationModes.items;

            const salesMethodsMap = new Map<string, any>(
                templateSalesMethods.map((item: any) => [item.id, item])
            );
            const salesMethodsOrderMap = new Map<string, number>(
                templateSalesMethods.map((item: any, idx: number) => [
                    item.id,
                    idx,
                ])
            );
            const reglementsMap = new Map<string, any>(
                templateReglements.map((item: any) => [item.id, item])
            );
            const reglementsOrderMap = new Map<string, number>(
                templateReglements.map((item: any, idx: number) => [
                    item.id,
                    idx,
                ])
            );
            const languagesMap = new Map<string, any>(
                templateLanguages.map((item: any) => [item.id, item])
            );
            const languagesOrderMap = new Map<string, number>(
                templateLanguages.map((item: any, idx: number) => [
                    item.id,
                    idx,
                ])
            );

            saleMethods.sort(
                (a, b) =>
                    (salesMethodsOrderMap.get(a.id) ?? Infinity) -
                    (salesMethodsOrderMap.get(b.id) ?? Infinity)
            );
            saleMethods.forEach((method) => {
                const templateItem = salesMethodsMap.get(method.id);
                if (templateItem) {
                    method.active = templateItem.active;
                    method.languages = templateItem.languages;
                }
            });

            reglements.sort(
                (a, b) =>
                    (reglementsOrderMap.get(a.id) ?? Infinity) -
                    (reglementsOrderMap.get(b.id) ?? Infinity)
            );
            reglements.forEach((reglement) => {
                const templateItem = reglementsMap.get(reglement.id);
                if (templateItem) {
                    reglement.active = templateItem.active;
                    reglement.languages = templateItem.languages;
                }
            });

            if (informationModes.length > 0) {
                const infoModesMap = new Map<string, any>(
                    templateInfoModes.map((item: any) => [item.id, item])
                );
                const infoModesOrderMap = new Map<string, number>(
                    templateInfoModes.map((item: any, idx: number) => [
                        item.id,
                        idx,
                    ])
                );

                informationModes.forEach((mode) => {
                    const templateItem = infoModesMap.get(mode.id);
                    if (templateItem) {
                        mode.active = templateItem.active;
                        mode.languages = templateItem.languages;
                    }
                });

                informationModes.sort(
                    (a, b) =>
                        (infoModesOrderMap.get(a.id) ?? Infinity) -
                        (infoModesOrderMap.get(b.id) ?? Infinity)
                );

                const infoModeBySaleMethod = new Map(
                    templateSalesMethods.map((method: any) => [
                        method.name,
                        method.informationModes?.items ?? [],
                    ])
                );

                saleMethods.forEach((method) => {
                    if (method.name && method.informationModes) {
                        const syncedItems = infoModeBySaleMethod.get(
                            method.name
                        );
                        if (syncedItems)
                            method.informationModes.items = syncedItems;
                    }
                });
            }

            languages.sort(
                (a, b) =>
                    (languagesOrderMap.get(a.id) ?? Infinity) -
                    (languagesOrderMap.get(b.id) ?? Infinity)
            );
            languages.forEach((lang) => {
                const templateLang = languagesMap.get(lang.id);
                if (!templateLang) return;

                lang.active = templateLang.active;
                const isNotDisabled = !itemsDisable.some(
                    (disabled: any) =>
                        disabled.id === franchiseID &&
                        disabled.shopId === shopId
                );
                lang.isDefault =
                    templateLang.isDefault && lang.active && isNotDisabled;
            });

            const defaultLang = languages.find((l) => l.isDefault);
            if (defaultLang) {
                languages.forEach((lang) => {
                    lang.isDefault = lang.id === defaultLang.id;
                });
            }

            setIsInformationModesEmpty(
                !hasValidInformationModes(informationModes, saleMethods)
            );

            return {
                ...templateProject,
                content: {
                    ...templateProject.content,
                    languages: {
                        ...templateProject.content.languages,
                        items: orderLanguages(languages),
                    },
                    salesMethods: { items: saleMethods },
                    meansOfPayment: { items: reglements },
                    informationModes: { items: informationModes },
                },
            };
        } catch (error) {
            console.error("Error in getDataFromGeneralSetting:", error);
            return undefined;
        }
    };

    const handleValidate = async () => {
        setIsLoading(true);
        const projectObject = generalConfigStore.project;
        const apiUrl = `${API_BASE}/settings/project/template`;

        try {
            const requests = await Promise.all(
                rowData.subRows.map(async (el: any) => {
                    const shopProject = await getDataFromGeneralSetting(
                        el.id_boutique,
                        projectObject.template
                    );
                    return fetchGlobal(apiUrl, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            userId: userID,
                            franchiseId: franchiseID,
                            projectId: projectObject.projectId,
                            project_id: "PROJECT",
                            shopId: el.id_boutique,
                            operatorId: operatorID,
                            uuidUser,
                            modified_at: moment().format(
                                "DD/MM/YYYY [at] h:mm a"
                            ),
                            data: {
                                template: shopProject,
                                files: projectObject.files,
                            },
                        }),
                    });
                })
            );

            const hasErrors = requests.some((result: any) => result?.error);
            if (!hasErrors) mutate(apiUrl, requests);
        } catch (error) {
            console.error(error);
            showErrorToast(t);
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpdateTemplateFromShopToFranchiseOnClickEvent = async () => {
        try {
            const kioskProject = generalConfigStore.project;
            const data: any = await fetchGlobal(
                `${API_BASE}/settings/projectData?userId=106&shopId=0&franchiseId=${franchiseID}&projectId=${projectId}`
            );

            if (!data?.[0]) throw new Error("No project data found");

            const dataProject = structuredClone(data[0]);
            const modifiedDataProject = structuredClone(dataProject);

            let differences = removeMirroredDiffs(
                processDifferences(dataProject, kioskProject)
            );
            const flags = processDifferenceFlags(differences);
            setDifferenceBetweenTwoObjects(
                buildUpdatedDifferences(differences, kioskProject, flags)
            );

            const { modifiedDataProject: updatedData } = processUpdatedData(
                modifiedDataProject,
                differences,
                "0",
                false,
                kioskProject
            );

            const saveUrl = `${API_BASE}/settings/project/template`;
            const result: any = await fetchGlobal(saveUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    userId: userID,
                    franchiseId: franchiseID,
                    projectId: updatedData.projectId,
                    project_id: "PROJECT",
                    shopId: "0",
                    operatorId: operatorID,
                    uuidUser,
                    modified_at: moment().format("DD/MM/YYYY [at] h:mm a"),
                    data: {
                        template: updatedData.template,
                        files: updatedData.files,
                    },
                }),
            });

            if (result?.error) throw new Error(result.message);

            mutate(saveUrl, result);
            showSuccessToast(t, "Template franchise saved successfully");
        } catch (error) {
            console.error(
                "Error in handleUpdateTemplateFromShopToFranchiseOnClickEvent:",
                error
            );
            showErrorToast(t);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchAssociateKeys = async () => {
        try {
            const result: any = await fetchGlobal(
                `${API_BASE}/settings/project/shops?franchiseId=${franchiseID}&projectId=${projectId}`
            );
            return result?.success ? result.data : [];
        } catch (error) {
            console.error("Error fetching associate keys:", error);
            return [];
        }
    };

    const handleTemplateOrFilesChange = async (isChanged: boolean) => {
        if (!isChanged) return;

        const isRowDataEmpty =
            rowData === undefined || Object.keys(rowData).length === 0;
        const shopsList = oneShop ? await fetchAssociateKeys() : [];
        const isRowDataFromShop = oneShop && shopsList.length === 1;
        const isRowDataTemplateIdEmpty = rowData?.template?.id === "";

        if (isRowDataFromShop) {
            await handleUpdateTemplateFromShopToFranchiseOnClickEvent();
        }

        if (oneShop && isRowDataEmpty) {
            await handleSaveTemplateFromShopsToFranchiseOnClickEvent([
                ...shopsList,
                "0",
            ]);
        } else if (oneShop && !isRowDataEmpty && !isRowDataTemplateIdEmpty) {
            await handleUpdateProjectShopsButtonOnClickEvent();
        } else if (
            !oneShop &&
            !isRowDataEmpty &&
            isRowDataTemplateIdEmpty &&
            rowData?.subRows?.length !== 0
        ) {
            await handleUpdateProjectButtonOnClickEvent();
            await handleValidate();
        } else {
            await handleUpdateProjectButtonOnClickEvent();
        }
    };

    return (
        <React.Fragment>
            <Modal
                centered
                backdrop="static"
                fade={false}
                scrollable
                toggle={toggle}
                isOpen={isModalOpened}
                className="modal-wrapper__clz"
            >
                <ModalHeader
                    toggle={toggle}
                    className="text-uppercase cursor__clz"
                >
                    <span className="pme_txt_popUpTitle">{title}</span>
                </ModalHeader>
                <ModalBody className="p-0" style={{ overflowY: "unset" }}>
                    {children}
                </ModalBody>
                <ModalFooter>
                    {isWaysTabActive && (
                        <StyledButton
                            rounded
                            className="m-2"
                            outline
                            variant={mainColor}
                            disabled={Object.values(items.ways).length === 0}
                            onClick={() => setModalVisibility(true)}
                        >
                            {t("See")}
                        </StyledButton>
                    )}
                    {isConsult ? (
                        <CustomMainColorButton
                            className="w-20"
                            rounded
                            variant="primary"
                            onClick={() => {
                                setIsModalOpened(false);
                                setIsProjectModesModalOpened(false);
                                setSelectedRows([]);
                            }}
                        >
                            {t("Close")}
                        </CustomMainColorButton>
                    ) : (
                        <CustomMainColorButton
                            disabled={
                                generalConfigStore.project.template.id === "" ||
                                isFieldRequiredEmpty ||
                                isDisabled
                            }
                            className={classnames("w-20 cmn_btn_SaveButton", {
                                "not-allowed-icon__clz": isDisabled,
                            })}
                            rounded
                            variant="primary"
                            onClick={async () => {
                                setIsDisabled(true);
                                const isTemplateOrFilesChanged = hasTemplateOrFilesChanged(
                                    initialProject,
                                    generalConfigStore.project
                                );

                                if (initialProject.template.id === "") {
                                    await firstConfiguration();
                                } else if (
                                    rowData?.subRows?.length > 0 &&
                                    isTemplateOrFilesChanged
                                ) {
                                    await handleUpdateProjectButtonOnClickEvent();
                                    setIsModalConfirmationConfigurationKiosk(
                                        true
                                    );
                                    setIsDisabled(false);
                                    setIsLoading(false);
                                } else {
                                    await handleTemplateOrFilesChange(
                                        isTemplateOrFilesChanged
                                    );
                                }

                                setIsProjectModesModalOpened(false);
                                setIsModalOpened(false);
                                setIsCreatedProject(false);
                            }}
                        >
                            {t("Save")}
                        </CustomMainColorButton>
                    )}
                </ModalFooter>
            </Modal>

            {isModalShown && (
                <Modala close={closeModal}>
                    <div
                        style={{
                            height: "940px",
                            display: "grid",
                            gridTemplateRows: "0.001fr 0.999fr",
                            marginTop: "-185px",
                            marginLeft: "-280px",
                            borderRadius: "9px",
                            background:
                                "linear-gradient(227.9deg, #1F1726 0%, #120E16 100%)",
                            boxShadow:
                                "-5px -5px 11px #221A2B, 5px 5px 11px #16101C",
                            position: "fixed",
                            zIndex: 500000,
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "start",
                                justifyContent: "end",
                                padding: "10px",
                            }}
                        >
                            <CrossIcon
                                fill="white"
                                height={10}
                                width={10}
                                onClick={closeModal}
                            />
                        </div>
                        <SwiperWrapper />
                    </div>
                </Modala>
            )}
        </React.Fragment>
    );
}

const getDiff = (
    type: string,
    initial: TemplateStructure,
    modified: TemplateStructure
): DiffEntry[] => {
    const modifiedItems = modified?.template?.content?.[type]?.items;
    const initialItems = initial?.template?.content?.[type]?.items;

    if (!Array.isArray(modifiedItems)) return [];

    const initialMap = new Map(
        Array.isArray(initialItems)
            ? initialItems.map((item) => [item.id, item])
            : []
    );
    const normalizedItems = modifiedItems.map(
        (item) => initialMap.get(item.id) ?? item
    );
    const differences = diff(normalizedItems, modifiedItems) as
        | DiffEntry[]
        | undefined;

    if (!differences?.length) return [];

    const basePath: (string | number)[] = [
        "template",
        "content",
        type,
        "items",
    ];

    return differences.map((entry) => {
        const path = [...basePath, ...(entry.path ?? [])];
        const index = path[4];
        return {
            ...entry,
            path,
            id:
                typeof index === "number"
                    ? modifiedItems[index]?.id ?? null
                    : null,
        };
    });
};

export const processDifferences = (initial: any, modified: any) => {
    let differences: any = diff(initial, modified);
    if (!differences) return [];

    Object.keys(modified.template.content)
        .filter(
            (item) =>
                item !== "TPAValina" &&
                item !== "generalDesign" &&
                item !== "finalMessage"
        )
        .forEach((el) => {
            const diffResult = getDiff(el, initial, modified);
            differences = differences.filter(
                (elt: any) => elt.path[2] !== el || elt.path.includes("id")
            );
            differences = [...differences, ...diffResult];
        });

    return differences;
};

export const processUpdatedData = (
    modifiedDataProject: any,
    differences: any,
    shopId: string,
    warn: boolean,
    modified: any
) => {
    const changedItems: any[] = [];
    differences.forEach((change: any) => {
        const { path } = change;
        const kind = change.item?.kind ?? change.kind;
        const rhs = change?.rhs ?? change?.item?.rhs ?? null;
        const deletedElement = change.item?.lhs ?? change.lhs ?? null;

        const data = applyChange(
            modifiedDataProject,
            path,
            kind,
            rhs,
            warn,
            changedItems,
            deletedElement,
            shopId,
            modified
        );
        modifiedDataProject = data.obj;
        warn = data.warn;
    });
    return { modifiedDataProject, warn };
};

export function applyChange(
    obj: any,
    path: any[],
    kind: string,
    value: any,
    warn: boolean,
    changedItems: any[],
    deletedElement: any,
    shopId: any,
    modified: any
) {
    const { itemsDisable } = snapshot(generalConfigStore);

    if (path.includes("standbyScreen") && path.includes("items")) {
        obj.template.pages.ways.standbyScreen.items =
            modified.template.pages.ways.standbyScreen.items;
        return { obj, warn };
    }

    if (path.includes("standbyStateScreen")) {
        obj.template.content.standbyStateScreen.items =
            modified.template.content.standbyStateScreen.items;
        return { obj, warn };
    }

    if (path.includes("files")) {
        if (value !== null) {
            const fileExists = obj.files.some((el: any) => el.id === value.id);
            const pathFileUndefined = obj.files[path[1]]?.id === undefined;

            if (!fileExists && pathFileUndefined) {
                obj.files.push(value);
            } else {
                const id = modified.files[path[1]]?.id;
                const index = obj.files.findIndex((el: any) => el.id === id);

                if (index !== -1) {
                    let target = obj.files[index];
                    for (let i = 2; i < path.length - 1; i++) {
                        target = target[path[i]];
                    }
                    if (path.length > 2) {
                        target[path[path.length - 1]] = value;
                    } else {
                        obj.files[index] = value;
                    }
                }
            }
        }
        return { obj, warn };
    }

    const isPagePath =
        path.includes("ways") ||
        path.includes("elements") ||
        path.includes("subSteps");
    const indexOfItems = path.indexOf("items");
    const itemOccurrence = path.filter((el: any) => el === "items").length;

    if (indexOfItems === -1) {
        if (isPagePath) {
            const pageKeys = Object.keys(obj.template.pages[path[2]] ?? {});
            if (pageKeys.includes(path[3]) || kind === "N") {
                let target = obj;
                for (let i = 0; i < path.length - 1; i++) {
                    if (target[path[i]] === undefined) target[path[i]] = {};
                    target = target[path[i]];
                }
                if (kind === "D") {
                    delete target[path[path.length - 1]];
                } else {
                    target[path[path.length - 1]] = value;
                }
            }
        } else {
            let targetObj = obj;
            let targetModified = modified;
            for (let i = 0; i < path.length - 1; i++) {
                targetObj = targetObj[path[i]];
                targetModified = targetModified[path[i]];
            }
            targetObj[path[path.length - 1]] =
                targetModified[path[path.length - 1]];
        }
        return { obj, warn };
    }

    if (path[1] === "content") {
        const indexOfId = path.indexOf("id");
        const pathToItems = path.slice(0, indexOfItems + 1);
        const getByPath = (source: any, p: any[]) =>
            p.reduce((acc, key) => acc?.[key], source);

        if (
            (indexOfId !== -1 &&
                !changedItems.includes(path[indexOfItems - 1])) ||
            itemOccurrence === 2
        ) {
            changedItems.push(path[indexOfItems - 1]);

            const items = getByPath(modified, pathToItems) ?? [];
            const itemsShop = getByPath(obj, pathToItems) ?? [];
            const itemsShopMap = new Map(
                itemsShop.map((item: any) => [item.id, item])
            );

            let newItems: any[] = [];

            if (path[2] !== "languages") {
                items.forEach((el: any) => {
                    const shopItem = itemsShopMap.get(el.id);
                    if (shopItem) newItems.push({ ...shopItem });
                });
            } else {
                const favoredFranchise = items.find((el: any) => el.isDefault)
                    ?.id;
                const isFavoredInShop = itemsShopMap.has(favoredFranchise);
                const isFavoredDisabled = itemsDisable.some(
                    (d: any) => d.id === favoredFranchise && d.shopId === shopId
                );

                if (isFavoredInShop && !isFavoredDisabled) {
                    items.forEach((el: any) => {
                        const shopItem: any = itemsShopMap.get(el.id);
                        if (!shopItem) return;

                        const isDisabled = itemsDisable.some(
                            (d: any) => d.id === el.id && d.shopId === shopId
                        );
                        const active =
                            el.isDefault && !shopItem.active && !isDisabled
                                ? true
                                : shopItem.active;

                        newItems.push({
                            ...shopItem,
                            isDefault: el.isDefault,
                            active,
                        });
                    });
                } else {
                    warn = true;
                    return { obj, warn };
                }
            }

            let target = obj;
            for (let i = 0; i < pathToItems.length - 1; i++) {
                target = target[pathToItems[i]];
            }
            target[pathToItems[pathToItems.length - 1]] = newItems;
        } else {
            const itemPath = [...pathToItems, path[indexOfItems + 1]];
            const modifiedItem = getByPath(modified, itemPath);
            const id = modifiedItem?.id ?? "";

            const shopItems = getByPath(obj, pathToItems) ?? [];
            const indexInShop = shopItems.findIndex((el: any) => el.id === id);

            const isDisabledItem =
                itemsDisable.some(
                    (el: any) => el.id === id && el.shopId === shopId
                ) && value === true;

            if (isDisabledItem) {
                warn = true;
            } else if (indexInShop !== -1) {
                const updatedPath = [...path];
                updatedPath[indexOfItems + 1] = indexInShop;

                const isFavored =
                    obj?.template?.content?.languages?.items?.[indexInShop]
                        ?.isDefault ?? false;
                const shouldSkip =
                    path[2] === "languages" &&
                    !path.includes("isDefault") &&
                    value === false &&
                    isFavored;

                if (shouldSkip) {
                    warn = true;
                } else {
                    let target = obj;
                    for (let i = 0; i < updatedPath.length - 1; i++) {
                        target = target[updatedPath[i]];
                    }
                    if (kind === "D") {
                        delete target[updatedPath[updatedPath.length - 1]];
                    } else {
                        target[updatedPath[updatedPath.length - 1]] = value;
                    }
                }
            }
        }
    }

    if (isPagePath && indexOfItems !== -1) {
        const pageKeys = Object.keys(obj.template.pages[path[2]] ?? {});
        if (pageKeys.includes(path[3]) || kind === "N") {
            let target = obj;
            for (let i = 0; i < path.length - 1; i++) {
                if (target[path[i]] === undefined) target[path[i]] = {};
                target = target[path[i]];
            }
            if (kind === "D") {
                delete target[path[path.length - 1]];
            } else {
                target[path[path.length - 1]] = value;
            }
        }
    }

    return { obj, warn };
}

export function removeMirroredDiffs(diffs: any[]) {
    const result = [...diffs];
    for (let i = 0; i < result.length; i++) {
        if (
            result[i].lhs !== undefined &&
            result[i].rhs !== undefined &&
            result[i].path[2] !== "languages" &&
            result[i].path[3] !== "items"
        ) {
            for (let j = i + 1; j < result.length; j++) {
                if (
                    result[j].lhs !== undefined &&
                    result[j].rhs !== undefined &&
                    result[j].path[2] !== "languages" &&
                    result[j].path[3] !== "items" &&
                    result[i].lhs === result[j].rhs &&
                    result[i].rhs === result[j].lhs &&
                    Math.abs(result[j].path[4] - result[i].path[4]) === 1
                ) {
                    result.splice(j, 1);
                    j--;
                }
            }
        }
    }
    return result;
}

function hasTemplateOrFilesChanged(
    initialProject: any,
    kioskProject: any
): boolean {
    return (
        diff(
            JSON.parse(JSON.stringify(initialProject)).template,
            JSON.parse(JSON.stringify(kioskProject)).template
        ) !== undefined ||
        diff(
            JSON.parse(JSON.stringify(initialProject)).files,
            JSON.parse(JSON.stringify(kioskProject)).files
        ) !== undefined
    );
}
